<template>
  <div class="home-dash">
    <stats-board :stats="stats" :board="isAdmin ? 'home-admin' : 'home-user'" page="home" :refreshing="refreshing" @refresh="refreshStats">
      <template #intro>
        <div class="zh-eyebrow"><span class="zh-dot"></span> {{ isAdmin ? 'Administrator' : 'Your forests' }} · updated {{ updatedLabel }}</div>
        <h1 class="zh-title">{{ greeting }}, <span>{{ name }}</span></h1>
        <p class="zh-sub">{{ isAdmin ? 'This is the whole platform right now: forests, carbon, credits and partners.' : 'This is how your forests are doing today. Make this page yours: move, resize and choose the cards.' }}</p>
      </template>
    </stats-board>

    <transition name="zfade">
      <div v-if="noForests" class="hd-empty">
        <span class="hd-empty-icon"><i class="fas fa-seedling"></i></span>
        <div>
          <strong>Your forests will grow here.</strong>
          <div>As soon as a forest is registered in your name you will see its CO2, trees and growth on this page.</div>
        </div>
        <router-link to="/areas-map" class="hd-empty-cta"><i class="fas fa-globe-europe"></i> Explore the forest map</router-link>
      </div>
    </transition>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import StatsBoard from '../components/Stats/StatsBoard.vue';
import statsData from '../components/Stats/statsData';
import { timeAgo } from '../components/Stats/theme';

/**
 * The home page of a signed-in user. Administrators get the whole platform (the Statistics cards, with their own
 * layout); everybody else sees only their own forests and wallet (GET /stats/me): no other customer's forests, no
 * splits, no whole trees.
 */
export default {
  name: 'HomeDashboard',
  components: { StatsBoard },
  mixins: [statsData],
  data() {
    return { clock: 0, clockTimer: null };
  },
  computed: {
    ...mapGetters('user', ['getWeb2ActiveAccount']),
    isAdmin() {
      const a = this.getWeb2ActiveAccount;
      return !!(a && a.role && a.role.name === 'Administrator');
    },
    name() {
      const a = this.getWeb2ActiveAccount || {};
      const first = (a.firstName || a.username || a.email || '').split(/[\s@]/)[0];
      return first ? first.charAt(0).toUpperCase() + first.slice(1) : 'welcome';
    },
    greeting() {
      void this.clock;
      const h = new Date().getHours();
      return h < 6 ? 'Good night' : h < 13 ? 'Good morning' : h < 20 ? 'Good afternoon' : 'Good evening';
    },
    updatedLabel() { void this.clock; return this.lastUpdated ? timeAgo(this.lastUpdated) : '…'; },
    noForests() {
      return !this.isAdmin && this.stats.overview && this.stats.overview.areasQty === 0;
    }
  },
  created() {
    if (this.isAdmin) this.startGlobal(); else this.startMine();
    this.clockTimer = setInterval(() => { this.clock += 1; }, 15000);
  },
  beforeDestroy() {
    clearInterval(this.clockTimer);
  }
};
</script>

<style scoped lang="scss">
@import '../components/Stats/intro.scss';

.home-dash { position: relative; }

.hd-empty {
  position: fixed;
  left: 50%;
  bottom: 28px;
  transform: translateX(-50%);
  z-index: 50;
  width: min(720px, calc(100% - 32px));
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 16px 18px;
  border-radius: 18px;
  background: var(--card-bg);
  border: 1px solid rgba(var(--accent-green-rgb), 0.35);
  box-shadow: var(--shadow-lg);
  font-size: 0.86rem;
  color: var(--text-muted);

  strong { color: var(--text-heading); }
}

.hd-empty-icon {
  width: 42px;
  height: 42px;
  flex: 0 0 42px;
  border-radius: 13px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  background: var(--accent-gradient);
}

.hd-empty-cta {
  margin-left: auto;
  white-space: nowrap;
  font-weight: 700;
  color: var(--accent-green);
}

.zfade-enter-active, .zfade-leave-active { transition: opacity 0.3s, transform 0.3s; }
.zfade-enter, .zfade-leave-to { opacity: 0; transform: translate(-50%, 10px); }

@media (max-width: 600px) {
  .hd-empty { flex-wrap: wrap; }
  .hd-empty-cta { margin-left: 0; }
}
</style>
